import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { useAuthContext } from '../../auth/AuthContext';
import { queryAthenaLookup } from '../../service/form';
import type { AthenaLookupEnvironment, AthenaLookupRow } from '../../service/types';
import config from '../../config';
import { getInstancePrefix } from '../../utils/autofill-math';
import AutocompleteWrapper from './Autocomplete/AutocompleteWrapper';
import {
  preserveViewportPosition,
  resolveSelectedLookupRow,
  scrollToFieldGroup,
  type StaffLookupRow,
  toStaffLookupRow,
} from './AthenaStaffLookup';

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

const MINIMUM_SEARCH_LENGTH = 2;
const DEFAULT_MATCH_LIMIT = 5;
const LOOKUP_RESULT_LIMIT = 25;
const LOOKUP_LOADING_MESSAGE = 'Searching records...';

export const getNominatorLookupTargetFields = (questionName: string) => {
  const prefix = getInstancePrefix(questionName);
  return {
    fullName: `${prefix}nominator-full-name`,
    email: `${prefix}nominator-email`,
  };
};

const getRuntimeEnvironment = (): AthenaLookupEnvironment => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const apiUrl = String(config.apiUrl || '').toLowerCase();
  return hostname.includes('preprod') || apiUrl.includes('preprod') ? 'preprod' : 'prod';
};

const formatLookupLabel = (row: StaffLookupRow): string =>
  `${row.name} (${row.email_address})`;

export default function AthenaNominatorLookup({
  question,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { fetchJWT } = useAuthContext();
  const { setValue, clearErrors } = useFormContext();
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupHint, setLookupHint] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const rowsByLabelRef = useRef<Record<string, StaffLookupRow>>({});
  const latestSearchRequestId = useRef(0);

  const targetFields = useMemo(
    () => getNominatorLookupTargetFields(question.name),
    [question.name],
  );

  const clearTargetFields = useCallback(() => {
    setValue(targetFields.fullName, '', { shouldDirty: false, shouldValidate: true });
    setValue(targetFields.email, '', { shouldDirty: false, shouldValidate: true });
  }, [setValue, targetFields]);

  const clearLookupSelection = useCallback(
    (shouldValidate = false) => {
      setValue(question.name, '', { shouldDirty: false, shouldValidate });
      if (!shouldValidate) {
        clearErrors(question.name);
      }
    },
    [clearErrors, question.name, setValue],
  );

  const populateTargetFields = useCallback(
    (row: StaffLookupRow) => {
      preserveViewportPosition();
      setValue(targetFields.fullName, row.name, { shouldDirty: false, shouldValidate: true });
      setValue(targetFields.email, row.email_address, { shouldDirty: false, shouldValidate: true });
      clearErrors([question.name, targetFields.fullName, targetFields.email]);
      preserveViewportPosition();
      scrollToFieldGroup(targetFields.fullName);
    },
    [clearErrors, question.name, setValue, targetFields],
  );

  const searchStaff = useCallback(
    async (query: string): Promise<string[]> => {
      const trimmedQuery = query.trim();
      const requestId = latestSearchRequestId.current + 1;
      latestSearchRequestId.current = requestId;

      if (trimmedQuery.length < MINIMUM_SEARCH_LENGTH) {
        rowsByLabelRef.current = {};
        setIsSearching(false);
        setLookupError(null);
        setLookupHint(
          `Enter at least ${MINIMUM_SEARCH_LENGTH} characters to search by name or email`,
        );
        clearTargetFields();
        clearLookupSelection(false);
        return [];
      }

      setLookupError(null);
      setLookupHint(null);
      setIsSearching(true);

      try {
        const { idToken } = await fetchJWT({ redirectOnFail: false });
        if (!idToken) {
          throw new Error('Missing auth token');
        }

        const response = await queryAthenaLookup(idToken, {
          lookupType: 'staffInPost',
          environment: getRuntimeEnvironment(),
          searchTerm: trimmedQuery,
          maxRows: LOOKUP_RESULT_LIMIT,
        });

        const rows = response.rows
          .map((row) => toStaffLookupRow(row as AthenaLookupRow))
          .filter((row): row is StaffLookupRow => row !== null);

        rowsByLabelRef.current = rows.reduce<Record<string, StaffLookupRow>>((acc, row) => {
          acc[formatLookupLabel(row)] = row;
          return acc;
        }, {});

        if (rows.length === 0) {
          clearTargetFields();
          clearLookupSelection(false);
          setLookupHint('No matching staff records found');
        }

        return rows.map(formatLookupLabel);
      } catch (_error) {
        rowsByLabelRef.current = {};
        clearTargetFields();
        clearLookupSelection(false);
        setLookupHint(null);
        setLookupError('Staff lookup is unavailable. Please try again later.');
        return [];
      } finally {
        if (latestSearchRequestId.current === requestId) {
          setIsSearching(false);
        }
      }
    },
    [clearLookupSelection, clearTargetFields, fetchJWT],
  );

  const handleValueConfirmed = useCallback(
    (value: string) => {
      const selectedRow = resolveSelectedLookupRow(value, rowsByLabelRef.current);
      if (!selectedRow) {
        clearTargetFields();
        clearLookupSelection(true);
        return;
      }

      setLookupError(null);
      setLookupHint(null);
      populateTargetFields(selectedRow);
    },
    [clearLookupSelection, clearTargetFields, populateTargetFields],
  );

  return (
    <div className={lookupError ? 'govuk-form-group govuk-form-group--error' : 'govuk-form-group'}>
      <AutocompleteWrapper
        id={anchorId || String(question.name)}
        name={question.name}
        title={question.title}
        rules={question.rules}
        placeholder={question.searchPlaceholder || question.placeholder}
        detailsHeading={detailsHeading}
        detailsContent={detailsContent}
        matchLimit={question.matchLimit ?? DEFAULT_MATCH_LIMIT}
        fuzzyMatch={question.fuzzyMatch ?? true}
        asyncSearch={searchStaff}
        isLoading={isSearching}
        loadingMessage={LOOKUP_LOADING_MESSAGE}
        minimumLoadingMessageLength={MINIMUM_SEARCH_LENGTH}
        onValueConfirmed={handleValueConfirmed}
      />
      {lookupHint && <p className="govuk-hint govuk-!-margin-top-2">{lookupHint}</p>}
      {lookupError && <p className="govuk-error-message govuk-!-margin-top-2">{lookupError}</p>}
    </div>
  );
}
